import type { ActivityModule, ActivityContext, ActivityHandle } from '../../core/activity';
import '../../shared/tens-complement.css';
import './style.css';
import levelsData from './levels.json';

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  questionsPerSubLevel: number;
  numberOfElements: number;
}

interface RowCard {
  id: number;
  value: number;
}

interface EquationRow {
  rowIdx: number;
  cards: RowCard[];
  totalSum: number;
  status: 'pending' | 'ok' | 'err';
  answer: string;
}

const LEVELS = levelsData.levels as Level[];

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function genRowCards(numberOfElements: number): RowCard[] {
  const pairs = Math.floor(numberOfElements / 2);
  const values: number[] = [];
  let safety = 100;
  while (values.length < pairs * 2 && safety-- > 0) {
    const v = randInt(1, 9);
    if (values.length < 10 && values.includes(v)) continue;
    values.push(v, 10 - v);
  }
  if (numberOfElements % 2 !== 0) values.push(randInt(1, 9));
  let shuffled = shuffle(values);
  let tries = 10;
  while (tries-- > 0 && shuffled[0]! + shuffled[1]! === 10) {
    shuffled = shuffle(values);
  }
  return shuffled.map((v, i) => ({ id: i, value: v }));
}

export const tensComplementCalculate: ActivityModule = {
  id: 'tens_complement_calculate',
  title: 'Calcule com complemento de 10',
  description: 'Forme pares somando 10 dentro dos parênteses e escreva a soma total da operação.',
  ageMin: 8,
  ageMax: 11,
  difficulty: 4,
  iconPath: 'assets/gcompris-AGPL/tens_complement_calculate/icon.svg',
  category: 'aritmetica',
  totalLevels: LEVELS.length,
  mount,
};

function mount(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0]!;
  let rows: EquationRow[] = [];
  let selected: { rowIdx: number; colIdx: number } | null = null;
  let locked = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Calcule com complemento de 10</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage tens">
      <div class="feedback" aria-live="polite"></div>
      <p class="tens__hint">Troque os números (mesma linha) pra formar pares somando <strong>10</strong> dentro dos <strong>(parênteses)</strong>, depois escreva a soma total no quadrado azul.</p>
      <div class="tens__equations"></div>
      <div class="tens__actions">
        <button class="btn btn--primary tens__ok" type="button">OK</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const backBtn = page.querySelector<HTMLButtonElement>('.activity-bar__back')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const equationsEl = page.querySelector<HTMLElement>('.tens__equations')!;
  const okBtn = page.querySelector<HTMLButtonElement>('.tens__ok')!;
  backBtn.addEventListener('click', () => ctx.onExit());
  okBtn.addEventListener('click', validate);

  initLevel();

  function initLevel(): void {
    rows = [];
    for (let i = 0; i < level.questionsPerSubLevel; i++) {
      const cards = genRowCards(level.numberOfElements);
      const total = cards.reduce((s, c) => s + c.value, 0);
      rows.push({ rowIdx: i, cards, totalSum: total, status: 'pending', answer: '' });
    }
    selected = null;
    locked = false;
    render();
  }

  function render(): void {
    equationsEl.innerHTML = '';
    for (const row of rows) {
      const rowEl = document.createElement('div');
      rowEl.className = `tens__equation tens__equation--${row.status}`;
      rowEl.style.flexWrap = 'wrap';
      const n = row.cards.length;
      const pairs = Math.floor(n / 2);
      for (let p = 0; p < pairs; p++) {
        rowEl.appendChild(parenEl('('));
        rowEl.appendChild(cardEl(row.rowIdx, p * 2));
        rowEl.appendChild(opEl('+'));
        rowEl.appendChild(cardEl(row.rowIdx, p * 2 + 1));
        rowEl.appendChild(parenEl(')'));
        if (p < pairs - 1) rowEl.appendChild(opEl('+'));
      }
      if (n % 2 !== 0) {
        rowEl.appendChild(opEl('+'));
        rowEl.appendChild(cardEl(row.rowIdx, n - 1));
      }
      rowEl.appendChild(opEl('='));
      rowEl.appendChild(answerInputEl(row));
      if (row.status !== 'pending') {
        const mark = document.createElement('span');
        mark.className = 'tens__feedback-mark';
        mark.textContent = row.status === 'ok' ? '✓' : '✗';
        mark.style.color = row.status === 'ok' ? 'var(--color-success)' : '#ef4444';
        rowEl.appendChild(mark);
      }
      equationsEl.appendChild(rowEl);
    }
  }

  function cardEl(rowIdx: number, colIdx: number): HTMLElement {
    const row = rows[rowIdx]!;
    const card = row.cards[colIdx]!;
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'tens__card';
    if (selected && selected.rowIdx === rowIdx && selected.colIdx === colIdx) {
      el.classList.add('tens__card--selected');
    }
    el.textContent = String(card.value);
    el.addEventListener('click', () => onCardClick(rowIdx, colIdx));
    return el;
  }

  function parenEl(t: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'tens__paren';
    el.textContent = t;
    return el;
  }
  function opEl(t: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'tens__op';
    el.textContent = t;
    return el;
  }
  function answerInputEl(row: EquationRow): HTMLElement {
    const input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'numeric';
    input.pattern = '[0-9]*';
    input.maxLength = 3;
    input.className = 'tens__answer';
    input.value = row.answer;
    input.placeholder = '?';
    input.addEventListener('input', () => {
      row.answer = input.value.replace(/[^0-9]/g, '');
      input.value = row.answer;
      row.status = 'pending';
    });
    return input;
  }

  function onCardClick(rowIdx: number, colIdx: number): void {
    if (locked) return;
    rows[rowIdx]!.status = 'pending';
    if (!selected) {
      selected = { rowIdx, colIdx };
      ctx.audio.play('click');
      render();
      return;
    }
    if (selected.rowIdx === rowIdx && selected.colIdx === colIdx) {
      selected = null;
      ctx.audio.play('click');
      render();
      return;
    }
    if (selected.rowIdx !== rowIdx) {
      selected = { rowIdx, colIdx };
      ctx.audio.play('click');
      render();
      return;
    }
    const row = rows[rowIdx]!;
    const a = row.cards[selected.colIdx]!;
    const b = row.cards[colIdx]!;
    row.cards[selected.colIdx] = b;
    row.cards[colIdx] = a;
    selected = null;
    ctx.audio.play('click');
    render();
  }

  function validate(): void {
    if (locked) return;
    let allOk = true;
    for (const row of rows) {
      let pairsOk = true;
      const pairs = Math.floor(row.cards.length / 2);
      for (let p = 0; p < pairs; p++) {
        const sum = row.cards[p * 2]!.value + row.cards[p * 2 + 1]!.value;
        if (sum !== 10) { pairsOk = false; break; }
      }
      const answerOk = row.answer !== '' && Number(row.answer) === row.totalSum;
      const ok = pairsOk && answerOk;
      row.status = ok ? 'ok' : 'err';
      if (!ok) allOk = false;
    }
    render();
    if (allOk) {
      locked = true;
      ctx.audio.play('win');
      showFeedback('✓ Tudo certo!', 'ok');
      window.setTimeout(finishLevel, 900);
    } else {
      ctx.audio.play('wrong');
      showFeedback('Confira os pares e o resultado.', 'err');
    }
  }

  function showFeedback(text: string, kind: 'ok' | 'err'): void {
    feedback.textContent = text;
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 1000);
  }

  function finishLevel(): void {
    ctx.onComplete(level.level, rows.length);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você fechou ${rows.length} operações.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) next.addEventListener('click', () => { window.location.hash = `/tens_complement_calculate/${level.level + 1}`; });
  }

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
